import { Link } from "react-router-dom";
import { zones } from "@/mocks/zones";
import type { Post } from "@/mocks/posts";

function getZoneName(zoneId: string) {
  return zones.find((zone) => zone.id === zoneId)?.name ?? "专区";
}

function formatReads(count: number) {
  if (count >= 10000) return `${(count / 10000).toFixed(1)}万`;
  return `${count}`;
}

interface PostCardProps {
  post: Post;
  variant?: "cover" | "list" | "wide";
  index?: number;
}

export default function PostCard({ post, variant = "cover", index }: PostCardProps) {
  const zoneName = getZoneName(post.zoneId);

  if (variant === "list") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex gap-4 md:gap-5 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4 transition-colors duration-300 hover:border-primary-300 cursor-pointer"
      >
        <div className="w-24 h-24 md:w-40 md:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={post.cover}
            alt={`${post.title} 配图`}
            title={`${post.title} - 爆料91专区合集`}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="text-primary-700 font-medium">{zoneName}</span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span>{post.date}</span>
          </div>
          <h3 className="mt-2 font-heading font-semibold text-base md:text-lg text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
            {post.title}
          </h3>
          <p className="mt-2 hidden md:block text-sm text-foreground-600 line-clamp-2">
            {post.excerpt}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatReads(post.readCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {post.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group relative flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 cursor-pointer"
      >
        <div className="relative w-full aspect-[16/9] overflow-hidden bg-background-200">
          <img
            src={post.cover}
            alt={`${post.title} 配图`}
            title={`${post.title} - 爆料91专区合集`}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 p-5 md:p-6">
            <div className="flex items-center gap-2 text-xs text-background-100">
              <span className="bg-primary-600 text-background-50 px-2 py-0.5 rounded-sm font-medium">
                {zoneName}
              </span>
              <span>{post.date}</span>
            </div>
            <h3 className="mt-3 font-heading font-bold text-xl md:text-2xl text-background-50 leading-snug line-clamp-2">
              {post.title}
            </h3>
          </div>
        </div>
        <div className="p-5">
          <p className="text-sm leading-relaxed text-foreground-600 line-clamp-2">{post.excerpt}</p>
          <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatReads(post.readCount)} 阅读
            </span>
            <span className="text-primary-700 font-medium flex items-center gap-1">
              阅读全文
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/post/${post.id}`}
      className="group flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 transition-colors duration-300 hover:border-primary-300 cursor-pointer"
    >
      <div className="w-full aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={post.cover}
          alt={`${post.title} 配图`}
          title={`${post.title} - 爆料91专区合集`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          {typeof index === "number" && (
            <span className="font-label text-accent-700 text-sm">
              {String(index + 1).padStart(2, "0")}
            </span>
          )}
          <span className="text-primary-700 font-medium">{zoneName}</span>
          <span className="w-1 h-1 rounded-full bg-background-400"></span>
          <span>{post.date}</span>
        </div>
        <h3 className="mt-2.5 font-heading font-semibold text-lg text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
          {post.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
          {post.excerpt}
        </p>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatReads(post.readCount)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {post.author}
          </span>
        </div>
      </div>
    </Link>
  );
}